import React, { Component, useEffect } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import PropTypes from 'prop-types';
import Body from '../Components/layout/Body';
import TestDriveSetupSidebar from './TestDriveSetupSidebar';
import TestDriveDevSidebar from './TestDriveDevSidebar';

const TD_SETUP_NAME_HELP = `
  <div>
    <p><strong>Purpose: </strong>A name for the setup is a handy label for the full access method setup (the device plus its setup) that you are trying in this Test-drive.  
    This helps you keep track of multiple setups.  
    </p>
    <p>
    <strong>For example</strong>, suppose you do one Test-drive with a trackball, using built-in button for the click, the right hand to roll the ball, and a midline position.  
    Then you do a second Test-drive with the same trackball, but with a separate switch on the left armrest for the click, both hands involved, and the trackball positioned midline.  
    Both of these are “trackballs”, but their setups are quite different.  
    You’d want to name them so that you can easily tell which is which when you compare the results later on, maybe something like “basic trackball” for the first and “trackball with left switch” for the second. 
    </p>
    <p>
    You can edit the name later on if you want. 
    </p>
  </div>
`;

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  activeAccessMethodName: PropTypes.string,
  activeSetupSelectionMethod: PropTypes.string,
  activeSetupAccessories: PropTypes.arrayOf(PropTypes.string),
  activeSetupBodySite: PropTypes.arrayOf(PropTypes.string),
  activeSetupPositioningNotes: PropTypes.string,
  activeSetupOtherNotes: PropTypes.string,
  activeSetupName: PropTypes.string,
  onChangeActiveSetupName: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  activeAccessMethodName: '',
  activeSetupSelectionMethod: '',
  activeSetupBodySite: [],
  activeSetupBodySiteComments: '',
  activeSetupAccessories: [],
  activeSetupPositioningNotes: '',
  activeSetupOtherNotes: '',
  activeSetupName: '',
};

//content sub-component with unique content for this page
class TestDriveSetupNameContent extends Component {

  //Render the form for asking about setup info.
  //Calls the setter that's passed in by the parent, when a radio button is clicked.
  //That setter changes the state of the vars in the parent, 
  //which triggers a new render of the view with the new var values.
  render(){
    //these can be changed:
    const activeSetupName = this.props.activeSetupName;
    const onChangeActiveSetupName = this.props.onChangeActiveSetupName;
   
    return(
    <>
    <Form>
      <FormGroup>
        <Label for="setup-name" className="aa-bold-text">
          Name for this access method setup:
        </Label>
        <Input
          id="setup-name"
          value={activeSetupName}
          onChange={onChangeActiveSetupName}
          name="setup-name"
          type="textfield"
        />
      </FormGroup>
    </Form>
    </>
  );
}
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function TestDriveSetupName(props) {
  //props for setup name section
  const activeAccessMethodName = props.activeAccessMethodName;
  const activeSetupSelectionMethod = props.activeSetupSelectionMethod;
  const activeSetupBodySite = props.activeSetupBodySite;
  const activeSetupAccessories = props.activeSetupAccessories;
  const activeSetupName = props.activeSetupName;
  const onChangeActiveSetupName = props.onChangeActiveSetupName;
  const saveFormData = props.saveFormData;
  const onContinue = props.onContinue;
  const dev = props.dev || {};

  console.log("rendering TestDriveSetupName, with props: " + JSON.stringify(props));

  const pageData = {
    image: "", //not yet used
    faIcon: "",
    pageSymbolClassName: "aa-section-symbol test-drive",
    title: `Enter a name for this setup`,
    subTitle: `You can give this setup a name that will help you remember it later. If you don't want to name it now, no problem - you'll have other chances to name it later on.`,
    learnMore: {linkText: "Learn More about naming your access method setup",
                title: "Setup name",
                content: TD_SETUP_NAME_HELP
                },
    nextRoute: "", //not used since we have an onContinue callback
    sidebar: true
  }

  useEffect(() => {
    console.log('TestDriveSetupName mounted');
    return () => {
      console.log('TestDriveSetupName unmounting and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <TestDriveSetupNameContent 
          activeSetupName={activeSetupName}
          onChangeActiveSetupName={onChangeActiveSetupName}
        />
      }
      sidebarComponent={
        dev.showDevSidebar 
        ?
        <TestDriveDevSidebar 
          activeTestDriveId={dev.tdId}
          activeTestDrive={dev.td}
          activeTestDriveActivityId={dev.actId}
          activeTestDriveActivities={dev.acts}
          completedTestDrives={dev.ctds}
        />  
        : 
        <TestDriveSetupSidebar 
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={activeSetupSelectionMethod}
          activeSetupBodySite={activeSetupBodySite}
          activeSetupAccessories={activeSetupAccessories}
          activeSetupName={activeSetupName}
       />
      }
      onContinue={onContinue} 
    />
    </>
  );
}

TestDriveSetupName.propTypes = propTypes;
TestDriveSetupName.defaultProps = defaultProps;

export default TestDriveSetupName;